import {
  DENTIST_HIDDEN_MENU_PAGES,
  isDentistUser,
  isSubClientAccount,
  SUB_ACCOUNT_HIDDEN_MENU_PAGES,
  TECHNICIAN_HIDDEN_MENU_PAGES,
  VENDOR_HIDDEN_MENU_PAGES,
} from './dentistPortal';
import { isTechnicianUser } from './orderSummaryVisibility';
import { isVendorAdminUser } from './authentication';

const ALWAYS_HIDDEN_MENU_NAMES = [
  'formal_types',
  'units',
  'unit_types',
  'branchs',
  'assets',
  'colors',
  'delivery_centers',
  'dentists',
  'wallets',
  'activities',
  'transactions',
  'expenses',
  'expense_types',
  'delivery_types',
  'delivery_type',
  'deliverytypes',
  'delivery-types',
];

const SUBSCRIPTION_CHILD = {
  name: 'setting.subscription',
  title: 'Subscription',
  path: '/setting/subscription',
  icon: 'credit_card',
  is_menu_item: true,
  translation_key: 'sidebar.setting_subscription',
};

export const buildHiddenMenuNames = (user) => {
  const hidden = new Set(ALWAYS_HIDDEN_MENU_NAMES);

  if (isDentistUser(user)) {
    DENTIST_HIDDEN_MENU_PAGES.forEach((name) => hidden.add(name));
    if (isSubClientAccount(user)) {
      SUB_ACCOUNT_HIDDEN_MENU_PAGES.forEach((name) => hidden.add(name));
    }
  } else {
    VENDOR_HIDDEN_MENU_PAGES.forEach((name) => hidden.add(name));
    if (isTechnicianUser(user)) {
      TECHNICIAN_HIDDEN_MENU_PAGES.forEach((name) => hidden.add(name));
    }
  }

  return hidden;
};

/**
 * Vendor users get Invoices inside the Finance sales section; the dentist portal keeps it top-level.
 */
const nestInvoicesUnderFinance = (menu) => {
  const invoices = menu.find((entry) => entry?.name === 'invoices');
  const hasFinance = menu.some((entry) => entry?.name === 'finance');
  if (!invoices || !hasFinance) {
    return menu;
  }

  const invoicesChild = {
    name: invoices.name,
    title: invoices.title,
    path: invoices.path,
    icon: invoices.icon,
    is_menu_item: true,
    meta: invoices.meta,
    translation_key: invoices.translation_key ?? 'sidebar.invoices',
  };

  return menu
    .filter((entry) => entry !== invoices)
    .map((entry) => {
      if (entry?.name !== 'finance') {
        return entry;
      }
      const children = Array.isArray(entry.children) ? entry.children : [];
      return { ...entry, children: [invoicesChild, ...children] };
    });
};

/**
 * Finance is a page container only; its children are shown as these top-level sections instead.
 * Page data stays nested under `finance` (permissions, routes); children not listed here become
 * top-level entries after the sections.
 */
const FINANCE_MENU_SECTIONS = [
  {
    name: 'finance_group.sales',
    title: 'Sales',
    icon: 'receipt',
    translation_key: 'sidebar.finance_group_sales',
    children: ['invoices', 'setting.discounts'],
  },
  {
    name: 'finance_group.expenses',
    title: 'Expenses & Suppliers',
    icon: 'payments',
    translation_key: 'sidebar.finance_group_expenses',
    children: [
      'setting.expenses',
      'setting.expense_types',
      'accounting.suppliers',
      'accounting.bills',
      'accounting.technician_settlements',
      'accounting.salaries',
    ],
  },
  {
    name: 'finance_group.cash_bank',
    title: 'Cash & Bank',
    icon: 'wallet',
    translation_key: 'sidebar.finance_group_cash_bank',
    children: ['accounting.cash_bank', 'accounting.reconciliation', 'setting.payment'],
  },
  {
    name: 'finance_group.accounting',
    title: 'Accounting',
    icon: 'menu_book',
    translation_key: 'sidebar.finance_group_accounting',
    children: [
      'accounting.chart_of_accounts',
      'accounting.journal',
      'setting.assets',
      'accounting.periods',
      'accounting.currencies',
      'accounting.tax',
    ],
  },
  {
    name: 'finance_group.reports',
    title: 'Reports & Insights',
    icon: 'assessment',
    translation_key: 'sidebar.finance_group_reports',
    children: [
      'revenue_report',
      'collection_report',
      'setting.transactions',
      'accounting.reports',
      'accounting.aging',
      'accounting.lab_costing',
      'accounting.insights',
    ],
  },
];

/**
 * Replaces the Finance entry with its sections as top-level entries.
 * Sections with a single visible child collapse into that child.
 */
const promoteFinanceSections = (menu) => {
  const financeIndex = menu.findIndex((entry) => entry?.name === 'finance');
  if (financeIndex < 0) {
    return menu;
  }

  const children = (menu[financeIndex].children ?? []).filter(
    (child) => child?.is_menu_item !== false
  );
  const byName = new Map(children.map((child) => [child?.name, child]));
  const placed = new Set();
  const promoted = [];

  FINANCE_MENU_SECTIONS.forEach(({ children: memberNames, ...section }) => {
    const members = memberNames.map((name) => byName.get(name)).filter(Boolean);
    members.forEach((member) => placed.add(member.name));

    if (members.length === 1) {
      promoted.push(members[0]);
    } else if (members.length > 1) {
      promoted.push({ ...section, is_menu_item: true, children: members });
    }
  });

  children.forEach((child) => {
    if (!placed.has(child?.name)) {
      promoted.push(child);
    }
  });

  const next = [...menu];
  next.splice(financeIndex, 1, ...promoted);
  return next;
};

const isAdminUser = isVendorAdminUser;

/**
 * Same visible sidebar entries the SideBar renders (including admin subscription).
 */
export const getVisibleSidebarMenu = (menu, user) => {
  if (!Array.isArray(menu)) {
    return [];
  }

  const hidden = buildHiddenMenuNames(user);
  const visible = menu.filter((entry) => !hidden.has(entry?.name));

  if (isDentistUser(user)) {
    return visible;
  }

  const filtered = promoteFinanceSections(nestInvoicesUnderFinance(visible));

  if (!isAdminUser(user)) {
    return filtered;
  }

  let hasPlatformSettings = false;
  const withSubscription = filtered.map((entry) => {
    if (entry?.name !== 'platform_settings') {
      return entry;
    }

    hasPlatformSettings = true;
    const children = Array.isArray(entry.children) ? [...entry.children] : [];
    const alreadyPresent = children.some(
      (child) =>
        child?.name === 'setting.subscription' ||
        child?.name === 'platform_settings.subscription' ||
        child?.path === '/setting/subscription'
    );

    if (alreadyPresent) {
      return entry;
    }

    return { ...entry, children: [...children, SUBSCRIPTION_CHILD] };
  });

  if (hasPlatformSettings) {
    return withSubscription;
  }

  const platformSettingsEntry = {
    name: 'platform_settings',
    title: 'Platform Settings',
    path: '/platform-settings',
    icon: 'category',
    is_menu_item: true,
    translation_key: 'sidebar.platform_settings',
    children: [SUBSCRIPTION_CHILD],
  };

  const labSettingsIndex = withSubscription.findIndex((entry) => entry?.name === 'setting');
  if (labSettingsIndex >= 0) {
    const next = [...withSubscription];
    next.splice(labSettingsIndex + 1, 0, platformSettingsEntry);
    return next;
  }

  return [...withSubscription, platformSettingsEntry];
};

const isTourableMenuItem = (entry) =>
  Boolean(entry?.path) && entry?.is_menu_item !== false && typeof entry.path === 'string';

/**
 * Flatten visible sidebar entries into navigable leaf pages for the system tour.
 */
export const flattenSidebarTourPages = (menu, user) => {
  const pages = [];

  getVisibleSidebarMenu(menu, user).forEach((entry) => {
    const children = Array.isArray(entry?.children)
      ? entry.children.filter((child) => child?.is_menu_item !== false)
      : [];

    if (children.length > 0) {
      children.forEach((child) => {
        if (isTourableMenuItem(child)) {
          pages.push({
            name: child.name,
            path: child.path,
            title: child.title ?? child.name,
            parentName: entry.name,
            translation_key: child.translation_key ?? child?.meta?.translationKey,
            meta: child.meta,
          });
        }
      });
      return;
    }

    if (isTourableMenuItem(entry)) {
      pages.push({
        name: entry.name,
        path: entry.path,
        title: entry.title ?? entry.name,
        parentName: null,
        translation_key: entry.translation_key ?? entry?.meta?.translationKey,
        meta: entry.meta,
      });
    }
  });

  return pages;
};

export const tourMenuTargetId = (name) =>
  `tour-menu-${String(name ?? '').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
